'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { shouldAdoptUrlQuery } from './coursePresentation';

interface ExploreSearchInputOptions {
  urlQuery: string;
  onCommit: (value: string) => void;
  delayMs?: number;
}

/** Typing owns the draft; browser history owns restoration, including while focused. */
export function useExploreSearchInput({ urlQuery, onCommit, delayMs = 220 }: ExploreSearchInputOptions) {
  const [value, setDraft] = useState(urlQuery);
  const draft = useRef(urlQuery);
  const editing = useRef(false);
  const mounted = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intent = useRef(0);
  const restoredQuery = useRef<{ value: string } | null>(null);
  const latest = useRef({ urlQuery, onCommit, delayMs });
  useLayoutEffect(() => { latest.current = { urlQuery, onCommit, delayMs }; });

  const cancelPending = useCallback(() => {
    intent.current += 1;
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const adopt = useCallback((next: string) => {
    draft.current = next;
    setDraft(next);
  }, []);

  useLayoutEffect(() => {
    mounted.current = true;
    const restore = () => {
      const next = new URLSearchParams(window.location.search).get('q') ?? '';
      cancelPending();
      restoredQuery.current = { value: next };
      editing.current = false;
      adopt(next);
    };
    window.addEventListener('popstate', restore);
    return () => {
      mounted.current = false;
      cancelPending();
      window.removeEventListener('popstate', restore);
    };
  }, [adopt, cancelPending]);

  useLayoutEffect(() => {
    // Next's URL props may arrive after the native history event. Do not let
    // the previous render put its query back while that restoration settles.
    if (restoredQuery.current) {
      if (urlQuery !== restoredQuery.current.value) return;
      restoredQuery.current = null;
    }
    if (shouldAdoptUrlQuery(draft.current, urlQuery, editing.current)) {
      cancelPending();
      adopt(urlQuery);
    }
  }, [urlQuery, adopt, cancelPending]);

  const setValue = useCallback((next: string) => {
    if (!mounted.current) return;
    editing.current = true;
    restoredQuery.current = null;
    cancelPending();
    adopt(next);
    if (next === latest.current.urlQuery) return;
    const currentIntent = intent.current;
    const delay = Number.isFinite(latest.current.delayMs) ? Math.max(0, latest.current.delayMs) : 220;
    timer.current = setTimeout(() => {
      timer.current = null;
      if (!mounted.current || currentIntent !== intent.current || next === latest.current.urlQuery) return;
      latest.current.onCommit(next);
    }, delay);
  }, [adopt, cancelPending]);

  const onFocus = useCallback(() => { editing.current = true; }, []);
  const onBlur = useCallback(() => {
    editing.current = false;
    // Blurring a just-typed draft still lets its intended debounce complete.
    if (timer.current !== null || restoredQuery.current) return;
    if (shouldAdoptUrlQuery(draft.current, latest.current.urlQuery, false)) adopt(latest.current.urlQuery);
  }, [adopt]);
  const submit = useCallback(() => {
    if (!mounted.current) return;
    cancelPending();
    restoredQuery.current = null;
    editing.current = false;
    const next = draft.current.trim();
    adopt(next);
    latest.current.onCommit(next);
  }, [adopt, cancelPending]);

  return { value, setValue, onFocus, onBlur, submit };
}
